import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import EditIcon from '@/letsgo/icons/Edit';
import DeleteIcon from '@/letsgo/icons/Delete';

interface TeamWorkspace {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
}

interface TeamWorkspaceManagementProps {
  teamId: string;
}

const TeamWorkspaceManagement: React.FC<TeamWorkspaceManagementProps> = ({ teamId }) => {
  const [workspaces, setWorkspaces] = useState<TeamWorkspace[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingWorkspace, setEditingWorkspace] = useState<TeamWorkspace | null>(null);
  const [workspaceName, setWorkspaceName] = useState<string>('');
  const [workspaceDescription, setWorkspaceDescription] = useState<string>('');

  const fetchWorkspaces = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/teams/${teamId}/workspaces`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to fetch workspaces: ${response.status}`);
      }

      const workspacesData = await response.json();
      setWorkspaces(workspacesData);
    } catch (err: any) {
      console.error("Error fetching workspaces:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (teamId) {
      fetchWorkspaces();
    }
  }, [teamId]);

  const handleOpenCreateModal = () => {
    setEditingWorkspace(null);
    setWorkspaceName('');
    setWorkspaceDescription('');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (workspace: TeamWorkspace) => {
    setEditingWorkspace(workspace);
    setWorkspaceName(workspace.name);
    setWorkspaceDescription(workspace.description || '');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingWorkspace(null);
  };

  const handleSaveWorkspace = async () => {
    if (!workspaceName) {
      setError('Workspace name cannot be empty.');
      return;
    }

    try {
      const method = editingWorkspace ? 'PUT' : 'POST';
      const url = editingWorkspace ? `/api/teams/${teamId}/workspaces/${editingWorkspace.id}` : `/api/teams/${teamId}/workspaces`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: workspaceName,
          description: workspaceDescription,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to save workspace: ${response.status}`);
      }

      console.log(`Workspace ${editingWorkspace ? 'updated' : 'created'} successfully!`);
      fetchWorkspaces(); // Refetch data
      handleCloseModal();
      // TODO: Add success notification
    } catch (err: any) {
      console.error('Error saving workspace:', err);
      setError(err.message);
      // TODO: Add error notification
    }
  };

  const handleDeleteWorkspace = async (workspaceId: string) => {
    if (!window.confirm('Are you sure you want to delete this workspace? All associated shared content will be unlinked.')) {
      return;
    }

    try {
      const response = await fetch(`/api/teams/${teamId}/workspaces/${workspaceId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to delete workspace: ${response.status}`);
      }

      console.log('Workspace deleted successfully!');
      fetchWorkspaces(); // Refetch data
      // TODO: Add success notification
    } catch (err: any) {
      console.error('Error deleting workspace:', err);
      setError(err.message);
      // TODO: Add error notification
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Shared Workspaces
        </Typography>
        <Button variant="contained" color="primary" onClick={handleOpenCreateModal}>
          Create New Workspace
        </Button>
      </Box>

      {workspaces.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', mt: 4 }}>
          No shared workspaces defined yet. Create your first workspace!
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="shared workspaces table">
            <TableHead>
              <TableRow>
                <TableCell>Workspace Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workspaces.map((workspace) => (
                <TableRow key={workspace.id}>
                  <TableCell>{workspace.name}</TableCell>
                  <TableCell>{workspace.description}</TableCell>
                  <TableCell>{new Date(workspace.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <IconButton aria-label="edit workspace" onClick={() => handleOpenEditModal(workspace)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton aria-label="delete workspace" onClick={() => handleDeleteWorkspace(workspace.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Shared Workspace Modal */}
      <Dialog open={isModalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>{editingWorkspace ? 'Edit Shared Workspace' : 'Create New Shared Workspace'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="workspace-name"
            label="Workspace Name"
            type="text"
            fullWidth
            variant="outlined"
            value={workspaceName}
            onChange={(e) => setWorkspaceName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="workspace-description"
            label="Description (Optional)"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={workspaceDescription}
            onChange={(e) => setWorkspaceDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleSaveWorkspace} variant="contained" color="primary">
            {editingWorkspace ? 'Save Changes' : 'Create Workspace'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamWorkspaceManagement;
